import type { EnvChoice } from './envSwitch';

/**
 * Which deploy is this build actually running on? (CORE-308)
 *
 * This used to be derived from `EXPO_PUBLIC_WEB_URL`, which is wrong on web:
 * the dev and preprod stands are built in **production mode**, so expo reads
 * `.env.production` and bakes `EXPO_PUBLIC_WEB_URL=https://agapella.io` into
 * their bundles too. Every error from rn-agape.vercel.app therefore arrived in
 * Sentry tagged `environment: production`, so the prod issue feed was a mix of
 * real user errors and dev-stand noise — and the ticket's «разделение
 * окружений» criterion was silently unmet. (The CI dev job already patches the
 * built output for the same class of bug — it rewrites the baked prod chat-api
 * host — but nothing fixed the web URL.)
 *
 * On web the serving hostname is the only thing that cannot lie about which
 * stand you are on, so callers pass `window.location.hostname`. Native has no
 * such hostname and keeps using the baked URL, which is correct there because
 * each EAS profile builds against its own backend.
 */
export type SentryEnvironment = 'production' | 'staging' | 'development' | 'preview';

/**
 * CORE-1391 — native test build (the one with the env switcher). QA builds are
 * baked with prod hosts, so the baked URL tagged every tester's session
 * `production`, even after switching the app to dev. Here the env follows the
 * switcher, and a test build left on prod is `preview`: only store builds
 * report to the production feed.
 */
export function testBuildSentryEnvironment(env: EnvChoice): SentryEnvironment {
  if (env === 'dev') return 'development';
  if (env === 'staging') return 'staging';
  return 'preview';
}

export function resolveSentryEnvironment(host: string | null | undefined): SentryEnvironment {
  const value = host ?? '';
  if (!value) return 'preview';
  // Prod is agapella.io and app.agapella.io alike (CORE-940 — the apex serves
  // the app itself, it no longer redirects). The RU edge serves prod under the
  // same domains, so geo-split traffic classifies too.
  if (value.includes('agapella.io')) return 'production';
  // Shared dev stand, plus the RU dev edge and the dev API domain
  // (*.tmless.org — e.g. app.rudev.tmless.org), which were also landing in the
  // production feed.
  // CORE-632 — the staging stand (*.ag1.tmless.org) reports now too; before the
  // generic `tmless.org` rule, or it would land in the development feed.
  if (value.includes('ag1.tmless.org')) return 'staging';
  if (value.includes('rn-agape.vercel.app') || value.includes('tmless.org')) return 'development';
  // Per-PR Vercel previews and rn-agape-preprod.vercel.app.
  return 'preview';
}
